import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Checkbox,
  useOutsideClick,
  useColorModeValue,
  Text,
  HStack,
  Button,
} from '@chakra-ui/react';
import { ChevronDownIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../api'; // Centralized API instance for requests

const CategoryFilter = ({ selectedCategories = [], onCategoryChange, onClose }) => {
  const [categories, setCategories] = useState([]);
  const dropdownRef = useRef();

  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/hierarchy/');
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  useOutsideClick({
    ref: dropdownRef,
    handler: onClose,
  });

  const isCategorySelected = (categoryId) =>
    selectedCategories.some((cat) => cat.id === categoryId);

  const determineSelectionState = (category) => {
    const isCurrentSelected = isCategorySelected(category.id);
    
    if (!category.children || category.children.length === 0) {
      // Leaf node - just check if it's selected
      return {
        isSelected: isCurrentSelected,
        isIndeterminate: false,
      };
    }

    // For parent nodes, check children states
    const childStates = category.children.map(child => determineSelectionState(child));
    const selectedChildren = childStates.filter(state => state.isSelected);
    const indeterminateChildren = childStates.filter(state => state.isIndeterminate);
    
    const allChildrenSelected = selectedChildren.length === childStates.length;
    const someChildrenSelected = selectedChildren.length > 0 || indeterminateChildren.length > 0;
    
    if (allChildrenSelected) {
      // All children are selected - parent should be selected
      return {
        isSelected: true,
        isIndeterminate: false,
      };
    } else if (someChildrenSelected) {
      // Some children are selected or indeterminate - parent should be indeterminate
      return {
        isSelected: false,
        isIndeterminate: true,
      };
    } else {
      // No children are selected - parent should be unselected
      return {
        isSelected: false,
        isIndeterminate: false,
      };
    }
  };

  const handleCategorySelection = (category, isSelected) => {
    const collectAllChildren = (cat) => {
      let children = [cat];
      if (cat.children && cat.children.length > 0) {
        cat.children.forEach(child => {
          children = children.concat(collectAllChildren(child));
        });
      }
      return children;
    };

    const findAllParents = (targetCategory, allCategories, parents = []) => {
      for (const cat of allCategories) {
        if (cat.children && cat.children.some(child => child.id === targetCategory.id)) {
          parents.push(cat);
          findAllParents(cat, allCategories, parents);
          break;
        }
        if (cat.children && cat.children.length > 0) {
          findAllParents(targetCategory, cat.children, parents);
        }
      }
      return parents;
    };

    const updateCategorySelection = (cat, selected, updatedCategories) => {
      if (selected) {
        // Add the category if not already selected
        if (!updatedCategories.some((item) => item.id === cat.id)) {
          updatedCategories.push({ id: cat.id, name: cat.name });
        }
      } else {
        // Remove the category if selected
        updatedCategories = updatedCategories.filter((item) => item.id !== cat.id);
      }
      return updatedCategories;
    };

    let updatedCategories = [...selectedCategories];

    if (isSelected) {
      // When selecting a category, select all its children
      const allChildren = collectAllChildren(category);
      allChildren.forEach(child => {
        updatedCategories = updateCategorySelection(child, true, updatedCategories);
      });
    } else {
      // When unselecting a category, unselect all its children
      const allChildren = collectAllChildren(category);
      allChildren.forEach(child => {
        updatedCategories = updateCategorySelection(child, false, updatedCategories);
      });
    }

    // Update parent states based on their children's selection
    const updateParentStates = (allCategories) => {
      const processCategory = (cat) => {
        if (cat.children && cat.children.length > 0) {
          // First process all children recursively
          cat.children.forEach(processCategory);
          
          // Then check the current category's state based on its children
          const childStates = cat.children.map(child => 
            updatedCategories.some(item => item.id === child.id)
          );
          
          const allChildrenSelected = childStates.length > 0 && childStates.every(Boolean);
          const someChildrenSelected = childStates.some(Boolean);
          
          if (allChildrenSelected) {
            // If all children are selected, select the parent
            if (!updatedCategories.some(item => item.id === cat.id)) {
              updatedCategories.push({ id: cat.id, name: cat.name });
            }
          } else if (!someChildrenSelected) {
            // If no children are selected, unselect the parent
            updatedCategories = updatedCategories.filter(item => item.id !== cat.id);
          } else {
            // If some children are selected, ensure parent is not selected (indeterminate state)
            updatedCategories = updatedCategories.filter(item => item.id !== cat.id);
          }
        }
      };

      allCategories.forEach(processCategory);
    };

    updateParentStates(categories);

    // Update the state via the onCategoryChange prop
    onCategoryChange(updatedCategories);
  };

  const renderCategories = (categoryList, level = 0) => {
    return categoryList.map((category) => {
      const { isSelected, isIndeterminate } = determineSelectionState(category);
      const hasChildren = category.children && category.children.length > 0;

      return (
        <Box key={category.id} w="full">
          {/* Category Item */}
          <AccordionItem border="none">
            <AccordionButton
              _hover={{ bg: hoverBg }}
              pl={level * 6 + 2}
              py="3"
              borderRadius="lg"
              transition="all 0.2s ease"
              color={textColor}
              justifyContent="flex-start"
              _expanded={{ bg: hoverBg }}
            >
              <Checkbox
                isChecked={isSelected}
                isIndeterminate={isIndeterminate}
                onChange={(e) => {
                  e.stopPropagation();
                  handleCategorySelection(category, !isSelected);
                }}
                mr="3"
                colorScheme="purple"
                size="md"
              />
              <Box flex="1" textAlign="left" fontWeight="medium" fontSize="sm">
                {category.name}
              </Box>
              {hasChildren && (
                <ChevronDownIcon 
                  color={subtleTextColor} 
                  transition="transform 0.2s ease"
                  _groupExpanded={{ transform: 'rotate(180deg)' }}
                />
              )}
            </AccordionButton>
            
            {/* Children Categories */}
            {hasChildren && (
              <AccordionPanel p={0}>
                <Accordion allowMultiple>
                  {renderCategories(category.children, level + 1)}
                </Accordion>
              </AccordionPanel>
            )}
          </AccordionItem>
        </Box>
      );
    });
  };

  return (
    <Box
      ref={dropdownRef}
      bg={cardBg}
      borderRadius="2xl"
      border="1px solid"
      borderColor={borderColor}
      boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
      position="relative"
      overflow="hidden"
      w="400px"
    >
      {/* Gradient accent */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="4px"
        bg="linear-gradient(90deg, #23a6d5, #23d5ab)"
      />
      
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
        <HStack justify="space-between" align="center">
          <Text 
            fontSize="lg" 
            fontWeight="bold" 
            color={textColor}
            bgGradient="linear(to-r, #23a6d5, #23d5ab)"
            bgClip="text"
          >
            🗂️ Фільтр категорій
          </Text>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            borderRadius="lg"
            color={subtleTextColor}
            _hover={{
              bg: hoverBg,
              color: textColor,
            }}
          >
            <CloseIcon boxSize={3} />
          </Button>
        </HStack>
      </Box>
      
      {/* Categories List */}
      <Box
        maxH="300px"
        overflowY="auto"
        p={4}
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            bg: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bg: borderColor,
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            bg: subtleTextColor,
          },
        }}
      >
        <Accordion allowMultiple>{renderCategories(categories)}</Accordion>
      </Box>
    </Box>
  );
};

export default CategoryFilter;
